import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Share2, Loader2, Eye } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { money } from "@/lib/api";
import { downloadBlob } from "@/lib/exporters";
import { buildStatement, statementHtml, statementText, monthRange, monthLabel, dateLabel } from "@/lib/consultantReport";

const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function ConsultantReport() {
  const { data, clinic, logoSrc, showMoney } = useApp();
  // Archived consultants are still reportable — their history is kept.
  const consultants = useMemo(() => [...data.consultants].sort((a, b) => (a.deleted ? 1 : 0) - (b.deleted ? 1 : 0) || a.name.localeCompare(b.name)), [data.consultants]);
  const [cid, setCid] = useState("");
  const [period, setPeriod] = useState("month"); // month | custom
  const [month, setMonth] = useState(thisMonth());
  const [range, setRange] = useState(() => monthRange(thisMonth()));
  const [busy, setBusy] = useState(false);

  const consultant = consultants.find((c) => c.id === (cid || consultants[0]?.id));
  const { from, to } = period === "month" ? monthRange(month) : range;
  const validRange = from && to && from <= to;
  const st = useMemo(
    () => (consultant && validRange ? buildStatement(consultant, data.visits, data.settlements, from, to) : null),
    [consultant, data.visits, data.settlements, from, to, validRange]
  );

  if (!showMoney) return <div className="dm-empty">Switch to Doctor Mode to view consultant reports.</div>;
  if (!consultants.length) return <div className="dm-empty">Add a consultant first to generate a report.</div>;

  const makeFile = async () => {
    setBusy(true);
    try { return await statementHtml(st, clinic, logoSrc); } finally { setBusy(false); }
  };

  const preview = async () => {
    const win = window.open("", "_blank");
    const file = await makeFile();
    const url = URL.createObjectURL(file);
    if (win) win.location.href = url; else window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const download = async () => {
    const file = await makeFile();
    downloadBlob(file, file.name);
  };

  // Phones hand the file itself to any app through the share sheet. Where sharing files isn't
  // supported, the statement is downloaded so it can be attached by hand.
  const canShareFiles = () => !!navigator.canShare?.({ files: [new File([""], "x.html", { type: "text/html" })] });
  const share = async () => {
    if (canShareFiles()) {
      const file = await makeFile();
      try { await navigator.share({ files: [file], title: file.name, text: statementText(st, clinic) }); } catch { /* cancelled */ }
      return;
    }
    await download();
    toast.success("Statement downloaded. Attach it to your message.");
  };

  const t = st?.totals;
  return (
    <div className="space-y-5" data-testid="consultant-report-page">
      <div>
        <Link to="/app/consultants" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800"><ArrowLeft size={14} /> Consultants</Link>
        <h1 className="mt-2 dm-page-title">
          Consultant <span className="font-extrabold">report</span>
        </h1>
        <p className="mt-2 text-sm text-slate-600">A statement you can send to a consultant. Patient names, phone numbers and notes are never included.</p>
      </div>

      <div className="dm-panel grid gap-5 p-5 md:grid-cols-4 md:items-end md:p-6" data-testid="report-filters">
        <div>
          <p className="label-xs mb-1.5">Consultant</p>
          <select data-testid="report-consultant" className="dm-input" value={consultant.id} onChange={(e) => setCid(e.target.value)}>
            {consultants.map((c) => <option key={c.id} value={c.id}>{c.name}{c.deleted ? " (archived)" : ""}</option>)}
          </select>
        </div>
        <div>
          <p className="label-xs mb-1.5">Period</p>
          <select data-testid="report-period" className="dm-input" value={period} onChange={(e) => { setPeriod(e.target.value); if (e.target.value === "custom") setRange(monthRange(month)); }}>
            <option value="month">Month</option>
            <option value="custom">Custom dates</option>
          </select>
        </div>
        {period === "month" ? (
          <div>
            <p className="label-xs mb-1.5">Month</p>
            <input type="month" data-testid="report-month" className="dm-input" value={month} max={thisMonth()} onChange={(e) => e.target.value && setMonth(e.target.value)} />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 md:col-span-2">
            <div>
              <p className="label-xs mb-1.5">From</p>
              <input type="date" data-testid="report-from" className="dm-input" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
            </div>
            <div>
              <p className="label-xs mb-1.5">To</p>
              <input type="date" data-testid="report-to" className="dm-input" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
            </div>
          </div>
        )}
      </div>

      {!st ? (
        <div className="dm-empty">Pick a start date on or before the end date.</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2" data-testid="report-actions">
            <button data-testid="report-download" onClick={download} disabled={busy} className="dm-btn-primary">{busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download statement</button>
            <button data-testid="report-share" onClick={share} disabled={busy} className="dm-btn-ghost"><Share2 size={15} /> Share</button>
            <button data-testid="report-preview" onClick={preview} disabled={busy} className="dm-btn-ghost"><Eye size={15} /> Preview</button>
          </div>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="report-totals">
            {[["Cases", t.cases], ["Consultant fee", money(t.fee)], ["Opening balance", money(t.opening)],
              ["Paid this period", money(t.paid)], ["Pending", money(t.pending)]].map(([l, v], i, all) => (
              <div key={l} className={`dm-panel p-4 ${i === all.length - 1 ? "col-span-2 md:col-span-1" : ""}`}>
                <p className="label-xs">{l}</p>
                <p className={`mt-1 font-mono text-lg font-semibold ${i === all.length - 1 && t.pending > 0 ? "text-red-700" : "text-slate-800"}`}>{v}</p>
              </div>
            ))}
          </div>

          {st.months.length > 1 && (
            <Table title="Month-wise totals" head={["Month", "Cases", "Consultant fee", "Paid"]}
              rows={st.months.map((m) => [monthLabel(m.month), m.cases, money(m.fee), money(m.paid)])}
              foot={["Total", t.cases, money(t.fee), money(t.paid)]} />
          )}
          <Table title="By treatment" head={["Treatment", "Cases", "Consultant fee"]}
            rows={st.treatments.map((r) => [r.treatment, r.cases, money(r.fee)])}
            foot={["Total", t.cases, money(t.fee)]} empty="No cases in this period." />
          <Table title="Cases" head={["#", "Date", "Treatment", "Consultant fee"]} left={3}
            rows={st.lines.map((l, i) => [i + 1, dateLabel(l.date), l.treatment, money(l.fee)])}
            foot={["", "", "Total", money(t.fee)]} empty="No cases in this period." />
          <Table title="Payments" head={["Date", "Note", "Amount"]} left={2}
            rows={st.payments.map((p) => [dateLabel(p.date), p.note || "—", money(p.amount)])}
            foot={["Total paid", "", money(t.paid)]} empty="No payments in this period." />
        </>
      )}
    </div>
  );
}

function Table({ title, head, rows, foot, empty, left = 1 }) {
  const align = (i) => (i < left ? "text-left" : "text-right");
  return (
    <div className="dm-panel overflow-hidden">
      <div className="dm-panel-head"><h2 className="font-head text-lg font-semibold text-slate-800">{title}</h2></div>
      {rows.length === 0 ? <div className="dm-empty m-4">{empty}</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-xs text-slate-500">{head.map((h, i) => <th key={h} className={`px-5 py-2.5 font-semibold ${align(i)}`}>{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r, j) => <tr key={j}>{r.map((c, i) => <td key={i} className={`px-5 py-2.5 ${i < left ? "text-slate-700" : "font-mono text-slate-800"} ${align(i)}`}>{c}</td>)}</tr>)}
            </tbody>
            {foot && <tfoot><tr className="border-t border-slate-300 font-semibold">{foot.map((c, i) => <td key={i} className={`px-5 py-2.5 ${i < left ? "" : "font-mono"} ${align(i)}`}>{c}</td>)}</tr></tfoot>}
          </table>
        </div>
      )}
    </div>
  );
}
